import React, { useCallback, useState, createContext, useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import {
  addStore,
  loginRest,
  signupRestaurant,
  ListStore,
  verifyOtpRest, // Ensure this is exported from your apiHandler.js
  forgotPasswordRequestRest,
  resetPasswordRest
} from "./apiHandler";
import { useSnackbar } from "./SnackbarProvider";
import { storeTokens, clearTokens, startTokenRefreshTimer, stopTokenRefreshTimer } from "../utils/tokenManager";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [, setda] = useLocalStorage('da', null);
  const [, setStore] = useLocalStorage('ds', null);
  const [listStore, setListStore] = useState([]);
  const [listFRN, setListFRN] = useState([]);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  // Start token refresh timer when user is logged in
  useEffect(() => {
    if (user?.jwt) {
      startTokenRefreshTimer();
    } else {
      stopTokenRefreshTimer();
    }

    // Cleanup on unmount
    return () => {
      stopTokenRefreshTimer();
    };
  }, [user]);

  const Logout = useCallback((stateCode) => {
    console.warn('Logout triggered with state:', stateCode);
    
    // Clear tokens using token manager
    clearTokens();
    stopTokenRefreshTimer();
    
    if (stateCode === 500) {
      navigate("/admin", { replace: true });
    } else if (stateCode === 403) {
      setUser(null);
      setda(null);
      setStore(null);
      navigate("/store", { replace: true });
    } else {
      setUser(null);
      setda(null);
      setStore(null);
      navigate("/login", { replace: true });
    }
  }, [setUser, setStore, setda, navigate]);

  /**
   * STEP 1: Initial Login
   * Checks credentials and triggers OTP generation on the backend.
   */
  const login = useCallback(async (data) => {
    try {
      const resp = await loginRest(data, Logout);

      if (resp.code === 0) {
        // If backend says verification is required, we don't set user yet
        if (resp.data?.step === 'verification_required') {
          showSnackbar(resp.msg || 'Verification code sent to email', 'info');
          return resp; // Success Step 1
        }

        // Legacy/Direct login fallback (if no OTP required)
        // await setUser(resp.data);
        // showSnackbar('Login successful!', 'success');
        // return true;
      } else {
        showSnackbar(resp.msg || 'Login failed', 'error');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      showSnackbar(error?.msg || 'Internal Server Error', 'error');
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser, showSnackbar, Logout]);

  /**
   * STEP 2: Verify OTP
   * Validates the 6-digit code and receives the final JWT + refresh token.
   */
  const verifyOTP = useCallback(async ({ email, otp, type }) => {
    try {
      // Calling the backend endpoint you provided in the router (/verify-otp)
      const resp = await verifyOtpRest({ email, otp, type }, Logout);

      if (resp && resp.code === 0) {
        // Handle different verification types
        if (type === 'login') {
          // Login verification: resp.data contains { jwt, token, refreshToken, userID, sAccess }
          const tokenData = {
            jwt: resp.data.jwt || resp.data.token,
            token: resp.data.token || resp.data.jwt,
            refreshToken: resp.data.refreshToken,
            userID: resp.data.userID,
            sAccess: resp.data.sAccess,
            email: email
          };
          
          // Store tokens securely using token manager
          storeTokens(tokenData);
          
          // Also update React state
          await setUser(tokenData);
          
          showSnackbar(resp?.msg || 'Verification successful. Welcome!', 'success');
          return resp;
        } else if (type === 'password_reset') {
          // Password reset verification: resp.data contains { email }
          showSnackbar(resp?.msg || 'Verified successfully. Proceed to reset password.', 'success');
          return resp;
        }
      } else {
        showSnackbar(resp?.msg || 'Invalid verification code', 'error');
        return resp;
      }
    } catch (error) {
      console.error("OTP Verification Error:", error);
      showSnackbar('Verification failed. Please try again.', 'error');
      return { code: 4, msg: 'Error' };
    }
  }, [setUser, showSnackbar, Logout]);

  const signup = useCallback(async (data) => {
    try {
      const resp = await signupRestaurant(data);
      if (resp.code === 0) {
        showSnackbar('Account created successfully!', 'success');
        return true;
      } else {
        showSnackbar('Error: ' + resp.msg, 'error');
      }
    } catch (error) {
      showSnackbar(error.msg || 'Signup failed', 'error');
    }
  }, [showSnackbar]);

  const updateStoreList = useCallback(async () => {
    const resp = await ListStore(Logout);
    if (resp?.data) {
      setListStore(resp.data.stores);
      setListFRN(resp.data.franchises)
    }
  }, [Logout]);

  const forgotPasswordRequest = useCallback(async (data) => {
    try {
      const resp = await forgotPasswordRequestRest(data, Logout);
      if (resp?.code === 0) {
        showSnackbar(resp.msg || 'Reset code sent to your email', 'info');
      } else {
        showSnackbar(resp?.msg || 'Failed to send reset code', 'error');
      }
      return resp;
    } catch (error) {
      showSnackbar('Error processing request', 'error');
    }
  }, [showSnackbar, Logout]);

  
  /**
   * FORGOT PASSWORD - STEP 3: Update Password
   */
  const resetPassword = useCallback(async (data) => {
    try {
      const resp = await resetPasswordRest(data, Logout);
      if (resp?.code === 0) {
        showSnackbar('Password updated successfully. Please log in.', 'success');
      } else {
        showSnackbar(resp?.msg || 'Failed to update password', 'error');
      }
      return resp;
    } catch (error) {
      showSnackbar('Reset failed', 'error');
    }
  }, [showSnackbar, Logout]);


  const addcmpny = useCallback(async (data) => {
    try {
      const resp = await addStore(data, Logout);
      if (resp.code === 0) {
        setListStore(resp.data);
        await setUser(resp.newLoad);
        showSnackbar('Store added successfully!', 'success');
        return true;
      } else {
        showSnackbar('Error: ' + resp.msg, 'error');
      }
    } catch (error) {
      showSnackbar(error.msg || 'Failed to add store', 'error');
    }
  }, [showSnackbar, Logout, setUser]);

  const value = useMemo(
    () => ({
      user,
      listStore,
      listFRN,
      login,
      verifyOTP,
      signup,
      Logout,
      addcmpny,
      updateStoreList,
      forgotPasswordRequest,
      resetPassword
    }),
    // ✅ Fixed: Added all missing dependencies
    [user, listStore, listFRN, login, verifyOTP, signup, Logout, 
     addcmpny, updateStoreList, forgotPasswordRequest, resetPassword]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};